import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid } from 'react-virtualized'
import 'react-virtualized/styles.css' // Importa lo stile di react-virtualized
import { AppCurrency, generateArticleKey, InventoryArticle } from 'shared'
import { VisualArticle } from './VisualArticle'

/* ResponsiveGrid uses react-virtualized's fixed grid;
  it is made responsive through two calculations: the first is the size of the window,
  which allows you to know how wide it is, the second is the size of the card set by the user.
  when the user changes the size of the cards, the number of available columns is recalculated
  based on the space occupied by each card and the size of the window. the work area in which
  the cards are distributed takes into account the size of both the sidebar and the filter panel */

interface ResponsiveGridInterface {
  items: any
  selectedSize: number
  openPriceSuggestDrawer?: (article: InventoryArticle, view?: 'buyPrice' | 'sellPrice') => void
  openReservedQuantityDrawer: (article: InventoryArticle) => void
  selectArticle: (article: InventoryArticle) => void
  onDeleteArticle?: (article: InventoryArticle) => void
  revertArticleEdit: (article: InventoryArticle) => void
  showLocations: (article: InventoryArticle) => void
  currency: AppCurrency
  parentPage?: string
}

//these values are in css file. these are the minimum width of cards
// 22 is padding between cards
//1.17 is the height-to-width ratio of the cards
//when cards are small is better use more distance between cards because if there are lots of modifications cards became longer
const getCellDimensions = (selectedSize: number): [number, number] => {
  switch (selectedSize) {
    case 9:
      return [345 + 22, Math.ceil(345 * 1.19 + 22)]
    case 8:
      return [321 + 22, Math.ceil(321 * 1.19 + 22)]
    case 7:
      return [297 + 22, Math.ceil(297 * 1.19 + 22)]
    case 6:
      return [274 + 22, Math.ceil(274 * 1.19 + 22)]
    case 5:
      return [260 + 22, Math.ceil(250 * 1.21 + 22)]
    case 4:
      return [243 + 22, Math.ceil(233 * 1.23 + 22)]
    case 3:
      return [225 + 22, Math.ceil(215 * 1.25 + 22)]
    case 2:
      return [215 + 22, Math.ceil(198 * 1.27 + 22)]
    case 1:
      return [195 + 18, Math.ceil(180 * 1.3 + 22)]
    default:
      return [260 + 22, Math.ceil(250 * 1.19 + 22)]
  }
}

// Fills from its own top to the bottom of the window, unless an ancestor claims the viewport with
// data-grid-viewport - then it stops at that box instead.
const availableHeight = (el: HTMLElement): number => {
  const top = el.getBoundingClientRect().top
  const viewport = el.closest('[data-grid-viewport]')
  const bottom = viewport ? viewport.getBoundingClientRect().bottom : window.innerHeight
  return Math.max(0, bottom - top - 4)
}

export const ResponsiveGrid = React.memo(function ResponsiveGrid({
  selectedSize,
  items,
  openPriceSuggestDrawer,
  openReservedQuantityDrawer,
  selectArticle,
  onDeleteArticle,
  revertArticleEdit,
  showLocations,
  currency,
  parentPage,
}: ResponsiveGridInterface): JSX.Element {
  const [containerWidth, setContainerWidth] = useState(window.innerWidth)
  const containerRef = useRef<HTMLDivElement>(null)
  const [gridHeight, setGridHeight] = useState(0)

  const visualArticleSizeClass = useMemo(() => {
    switch (selectedSize) {
      case 9:
        return 'nine'
      case 8:
        return 'eight'
      case 7:
        return 'seven'
      case 6:
        return 'six'
      case 4:
        return 'four'
      case 3:
        return 'three'
      case 2:
        return 'two'
      case 1:
        return 'one'
      default:
        return ''
    }
  }, [selectedSize])

  const [cellWidth, cellHeight] = useMemo(() => getCellDimensions(selectedSize), [selectedSize])

  const columnCount = useMemo(
    () => Math.max(1, Math.floor(containerWidth / (cellWidth || 1))),
    [containerWidth, cellWidth]
  )

  const rowCount = useMemo(
    () => Math.max(1, Math.ceil((items?.length || 1) / columnCount)),
    [items?.length, columnCount]
  )

  const gridWidth = containerWidth

  //update windows size when change, so we have responsive design
  useEffect(() => {
    let raf = 0
    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth)
        setGridHeight(availableHeight(containerRef.current))
      }
    }
    const onResize = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('resize', onResize)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  // remeasure when items change (HeaderRow appears/disappears, shifting the grid down/up)
  useEffect(() => {
    if (containerRef.current) {
      setGridHeight(availableHeight(containerRef.current))
    }
  }, [items?.length])

  const CellRenderer = useCallback(
    ({ columnIndex, key, rowIndex, style }: any) => {
      const itemIndex = rowIndex * columnCount + columnIndex
      const item = items[itemIndex]
      if (!item) return null

      return (
        <div
          key={key}
          data-testid={`cell-${itemIndex}`}
          data-custom-id={generateArticleKey(item) + '-' + item.location}
          style={{ ...style, height: cellHeight, width: cellWidth }}
        >
          <VisualArticle
            currency={currency}
            openPriceSuggestDrawer={openPriceSuggestDrawer}
            openReservedQuantityDrawer={openReservedQuantityDrawer}
            selectArticle={selectArticle}
            onDeleteArticle={onDeleteArticle}
            revertArticleEdit={revertArticleEdit}
            showLocations={showLocations}
            article={item}
            parentEl={parentPage}
            visualArticleSizeClass={visualArticleSizeClass}
          />
        </div>
      )
    },
    [
      items,
      columnCount,
      cellHeight,
      cellWidth,
      currency,
      openPriceSuggestDrawer,
      openReservedQuantityDrawer,
      selectArticle,
      onDeleteArticle,
      revertArticleEdit,
      showLocations,
      parentPage,
      visualArticleSizeClass,
    ]
  )

  return (
    <div ref={containerRef}>
      {gridHeight > 0 && (
        <Grid
          style={{ paddingBottom: '24px' }}
          columnCount={columnCount}
          columnWidth={cellWidth}
          height={gridHeight}
          rowCount={rowCount}
          rowHeight={cellHeight}
          width={gridWidth}
          cellRenderer={CellRenderer}
          overscanColumnCount={1}
          overscanRowCount={1}
        />
      )}
    </div>
  )
})
